import { SiteHeader } from '../components/SiteHeader';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { asset } from '../lib/asset';

export function Resume() {
  useDocumentMeta({ title: 'Resume | Tyler Wrenn' });

  return (
    <main>
      <SiteHeader />

      <div className="resume">
        <div className="resume__sheet">
          <section className="resume__section">
            <h2 className="resume__heading">Work Experience</h2>
            <p className="resume__clearance">Holds an Active Public Trust Clearance</p>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Data Engineering Sr Analyst</h3>
                <p className="resume-entry__period">July 2022 &mdash; Present</p>
                <p className="resume-entry__org">
                  <a href="https://www.accenturefederal.com/" target="_blank" rel="noopener noreferrer">
                    Accenture Federal Services
                  </a>
                  , Arlington, VA
                </p>
              </div>
            </article>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Technical Analyst</h3>
                <p className="resume-entry__period">February 2021 &mdash; July 2022</p>
                <p className="resume-entry__org">
                  <a href="https://www.longevityconsulting.com/" target="_blank" rel="noopener noreferrer">
                    Longevity Consulting
                  </a>
                  , Washington, D.C.
                </p>
              </div>
              <div className="resume-entry__body">
                <p className="resume-entry__sub">U.S. Department of Education</p>
                <ul>
                  <li>
                    Successfully moved a comprehensive website for the National Technical Assistance Center for the
                    Education of Youth Who Are Neglected, Delinquent, or At-Risk from development to production, meeting
                    strict deadlines.
                  </li>
                  <li>
                    Created a bot using Robot Process Automation that sorted over 5,000 files in a file system, cutting
                    the processing time by 50%.
                  </li>
                  <li>
                    Spearheaded and resolved critical project management workflows that could have resulted in over $110k
                    in accrued penalties.
                  </li>
                  <li>
                    Supported in the development of a proof-of-concept automation using RPA, AI, and OCR technology for
                    grants management processing which yielded over 600% decrease in processing time with 100% of all
                    errors captured.
                  </li>
                </ul>
                <p className="resume-entry__sub">Department of Transportation Canada</p>
                <ul>
                  <li>
                    Conducted a comprehensive current state assessment on the North American Fatigue Management Program's
                    transparency website which resulted in a full website redesign that yielded higher quality, security,
                    and accessibility for end users.
                  </li>
                </ul>
                <p className="resume-entry__sub">Internal Projects</p>
                <ul>
                  <li>
                    Successfully migrated Longevity's new corporate website from development to production as well as set
                    up Search Engine Optimization features, which has yielded an increase in customer outreach by over
                    20%.
                  </li>
                </ul>
              </div>
            </article>
          </section>

          <section className="resume__section">
            <h2 className="resume__heading">Certifications</h2>

            <article className="resume-entry resume-entry--cert">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">UiPath Certified Advanced RPA Developer (UiARD)</h3>
                <p className="resume-entry__org">Issued August 2021 &middot; Expires October 2026</p>
                <img
                  className="resume-entry__logo"
                  src={asset('assets/images/uipath-certified-advanced-rpa-developer.png')}
                  alt="UiPath Certified Advanced RPA Developer (UiARD) badge"
                />
              </div>
            </article>

            <article className="resume-entry resume-entry--cert">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">UiPath Certified Professional Specialized AI Professional</h3>
                <p className="resume-entry__org">Issued April 23, 2026 &middot; Expires April 20, 2029</p>
                <img
                  className="resume-entry__logo"
                  src={asset('assets/images/uipath-certified-professional-specialized-ai-professional.png')}
                  alt="UiPath Certified Professional Specialized AI Professional badge"
                />
              </div>
            </article>
          </section>

          <section className="resume__section">
            <h2 className="resume__heading">Education</h2>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">George Mason University</h3>
                <p className="resume-entry__period">December 2020</p>
                <p className="resume-entry__org">Fairfax, VA</p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>
                    Bachelor of Science, Information Technology, Concentration in Web Development and Multimedia, Volgenau
                    School of Engineering
                  </li>
                </ul>
              </div>
            </article>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">ECPI University</h3>
                <p className="resume-entry__period">June 2016</p>
                <p className="resume-entry__org">Richmond, VA</p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>Dual enrolled during senior year in high school</li>
                  <li>Completed the Web Development/Programming II course to further my expertise in programming</li>
                </ul>
              </div>
            </article>
          </section>

          <section className="resume__section">
            <h2 className="resume__heading">Technical Skills</h2>

            <div className="resume-skills">
              <h3 className="resume-skills__label">Applications and Platforms</h3>
              <ul className="resume-skills__list">
                {[
                  'Amazon Web Services',
                  'Adobe Dreamweaver',
                  'Adobe Photoshop',
                  'Notepad++',
                  'Microsoft Office',
                  'phpMyAdmin',
                  'File Transfer Protocol (FTP)',
                  'SSH File Transfer Protocol (SFTP)',
                  'Debian Linux (e.g. Ubuntu)',
                  'CentOS Linux',
                  'Red Hat Enterprise Linux',
                  'macOS',
                  'Windows',
                ].map((skill) => (
                  <li className="resume-badge" key={skill}>
                    {skill}
                  </li>
                ))}
              </ul>
            </div>

            <div className="resume-skills">
              <h3 className="resume-skills__label">Coding Languages</h3>
              <div className="resume-skills__group">
                <h4 className="resume-skills__sublabel">Proficient in:</h4>
                <ul className="resume-skills__list">
                  {['HTML', 'CSS', 'Bootstrap', 'MySQL', 'JavaScript', 'jQuery', 'PHP'].map((skill) => (
                    <li className="resume-badge" key={skill}>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="resume-skills__group">
                <h4 className="resume-skills__sublabel">Moderately Proficient in:</h4>
                <ul className="resume-skills__list">
                  {['C#', 'Visual Basic', 'ASP.NET MVC', 'MariaDB', 'Java'].map((skill) => (
                    <li className="resume-badge" key={skill}>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="resume-skills">
              <h3 className="resume-skills__label">Content Management Systems</h3>
              <ul className="resume-skills__list">
                {['WordPress', 'Drupal', 'Squarespace', 'cPanel', 'Other Website Builders'].map((skill) => (
                  <li className="resume-badge" key={skill}>
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="resume__section">
            <h2 className="resume__heading">Other Work Experience</h2>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Contract Website Designer</h3>
                <p className="resume-entry__period">January 2019 &mdash; August 2020</p>
                <p className="resume-entry__org">
                  Patient Experience and End Results Orthopedic Clinic (PEERClinic), Fairfax, VA
                </p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>
                    Redesigned the company website (
                    <a href="https://peerclinic.com/" rel="noopener noreferrer" target="_blank">
                      https://peerclinic.com/
                    </a>
                    )
                    <ul>
                      <li>Designed the company logo</li>
                    </ul>
                  </li>
                  <li>
                    Replaced the Electronic Medical Record system with an open-sourced medical record system called Open
                    Electronic Medical Records (OpenEMR)
                    <ul>
                      <li>
                        Provided continuous support to ensure the system is HIPPA and ONC (Office of the National
                        Coordinator for Health Information Technology) compliant
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </article>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Part-Time Deli Clerk</h3>
                <p className="resume-entry__period">June 2018 &mdash; August 2018</p>
                <p className="resume-entry__org">Publix, Henrico, VA</p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>Sliced deli meat and cheeses as well as cooked hot foods</li>
                  <li>Delivered effective customer engagement to provide quality of the order and accuracy of the detail</li>
                </ul>
              </div>
            </article>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Website Designer</h3>
                <p className="resume-entry__period">March 2016 &mdash; August 2016</p>
                <p className="resume-entry__org">Trolley House Refreshments, Richmond, VA</p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>
                    Overhauled the company's main website layout and content from scratch using Dreamweaver, making the
                    content more user-friendly to customers without the use of website creation software.
                  </li>
                  <li>
                    Assisted in the web analytics and Search Engine Optimization of the company's main website to further
                    expand the audience of their customers.
                  </li>
                </ul>
              </div>
            </article>
          </section>

          <section className="resume__section">
            <h2 className="resume__heading">Volunteer Experience</h2>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Project Mentor</h3>
                <p className="resume-entry__period">February 2020 &mdash; August 2020</p>
                <p className="resume-entry__org">
                  <a href="https://www.open-emr.org/" rel="noopener noreferrer" target="_blank">
                    Open Electronic Medical Records (OpenEMR)
                  </a>
                  , Modernizing UI, Google Summer of Code (GSoC)
                </p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>Took a leadership role for an open-sourced platform funded by its backers and sponsors</li>
                  <li>
                    Helped guide a student to redesign OpenEMR's User Interface, remove outdated code, and future-proof
                    the codebase
                  </li>
                  <li>Used effective interactions between individuals and administrators during GSoC</li>
                </ul>
              </div>
            </article>
          </section>

          <section className="resume__section">
            <h2 className="resume__heading">Side Projects</h2>

            <article className="resume-entry">
              <div className="resume-entry__head">
                <h3 className="resume-entry__role">Company Website</h3>
                <p className="resume-entry__period">Completed August 2019</p>
                <p className="resume-entry__org">
                  <a href="https://peersrva.com/" rel="noopener noreferrer" target="_blank">
                    Program for the Education and Enrichment of Relational Skills (PEERS RVA)
                  </a>
                  , Richmond, VA
                </p>
              </div>
              <div className="resume-entry__body">
                <ul>
                  <li>Executed and completed client needs for specific deliverables for student communication</li>
                  <li>Provided weekly updates to website progression</li>
                  <li>Created testing environment to ensure connectivity and user feasibility</li>
                </ul>
              </div>
            </article>
          </section>

          <footer className="resume__footer">
            <hr className="border-footer" />
            <p className="small">
              Designed by Tyler Wrenn
              <br />
              All copyrights are for their respected owners.
            </p>
          </footer>
        </div>
      </div>
    </main>
  );
}
